import React, { useEffect, useMemo } from "react";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { fileExtension, formatFileSize, isPreviewableImage } from "./helpdesk-files";

export interface HelpdeskPendingFilesProps {
  files: File[];
  onRemove: (file: File) => void;
  disabled?: boolean;
}

interface IPendingFilePreview {
  file: File;
  previewUrl: string | null;
}

/** Files picked but not sent yet: a thumbnail for raster images, the extension for anything else. */
const HelpdeskPendingFiles = ({
  files,
  onRemove,
  disabled = false
}: HelpdeskPendingFilesProps): React.ReactElement | null => {
  const previews: IPendingFilePreview[] = useMemo(
    (): IPendingFilePreview[] =>
      files.map((file: File): IPendingFilePreview => ({
        file,
        previewUrl: isPreviewableImage(file.type) ? URL.createObjectURL(file) : null
      })),
    [files]
  );

  useEffect(
    (): (() => void) => (): void => {
      previews.forEach((preview: IPendingFilePreview): void => {
        if (preview.previewUrl) URL.revokeObjectURL(preview.previewUrl);
      });
    },
    [previews]
  );

  if (files.length === 0) return null;

  return (
    <ul className="kuika-helpdesk-files" aria-label="Files to attach">
      {previews.map((preview: IPendingFilePreview, index: number): React.ReactElement => (
        <li
          key={`${preview.file.name}-${preview.file.size}-${preview.file.lastModified}-${index}`}
          className="kuika-helpdesk-files__item"
          title={`${preview.file.name} · ${formatFileSize(preview.file.size)}`}
        >
          <div className="kuika-helpdesk-files__thumb">
            {preview.previewUrl ? (
              <img src={preview.previewUrl} alt="" />
            ) : (
              <span className="kuika-helpdesk-files__ext">{fileExtension(preview.file.name)}</span>
            )}
          </div>
          <span className="kuika-helpdesk-files__name">{preview.file.name}</span>
          <button
            type="button"
            className="kuika-helpdesk-files__remove"
            aria-label={`Remove ${preview.file.name}`}
            disabled={disabled}
            onClick={(): void => onRemove(preview.file)}
          >
            <KuikaSvg name="x" width={12} height={12} />
          </button>
        </li>
      ))}
    </ul>
  );
};

export default HelpdeskPendingFiles;
